import { useState, useEffect, useCallback } from "react";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import {
	useGetListSyncTriggerQuery,
	useUpdateListSyncTriggerMutation,
	useGetOAuthStatusQuery,
} from "@/pages/Private/redux/hubspot/hubspot.api";

import { FeaturesCodes } from "../../../../Users/enum/codes";
import { HubspotSyncTriggerWithInherit } from "../../../../Company/schema/hubspot";

interface UseHubspotSyncTriggerOptions {
	listId: number;
}

export const useHubspotSyncTrigger = ({ listId }: UseHubspotSyncTriggerOptions) => {
	const isHubspotEnabled = useIsFeatureActive(FeaturesCodes.HUBSPOT);
	const { user } = useAppSelector(authSelector);
	const hasHubspotApiKey = user?.company?.hasHubspotApiKey ?? false;
	const { data: oauthStatus } = useGetOAuthStatusQuery(undefined, {
		skip: !isHubspotEnabled,
	});
	const isConnectedOAuth = oauthStatus?.connected ?? false;
	const isConnected = hasHubspotApiKey || isConnectedOAuth;

	// API hooks
	const { data: listSyncTrigger } = useGetListSyncTriggerQuery(listId, {
		skip: !isHubspotEnabled || !isConnected || !listId,
	});
	const [updateListSyncTrigger] = useUpdateListSyncTriggerMutation();

	// Local state for selected trigger
	const [selectedHubspotSyncTrigger, setSelectedHubspotSyncTrigger] = useState<
		HubspotSyncTriggerWithInherit | undefined
	>(undefined);

	// Initialize from API data
	useEffect(() => {
		if (listSyncTrigger) {
			setSelectedHubspotSyncTrigger(listSyncTrigger.trigger);
		}
	}, [listSyncTrigger]);

	// Check if sync trigger has changed
	const hasSyncTriggerChanged = useCallback(() => {
		return (
			isHubspotEnabled &&
			isConnected &&
			selectedHubspotSyncTrigger !== undefined &&
			selectedHubspotSyncTrigger !== listSyncTrigger?.trigger
		);
	}, [isHubspotEnabled, isConnected, selectedHubspotSyncTrigger, listSyncTrigger?.trigger]);

	// Save sync trigger to API
	const saveSyncTrigger = useCallback(async () => {
		if (hasSyncTriggerChanged() && selectedHubspotSyncTrigger) {
			await updateListSyncTrigger({
				listId,
				data: { trigger: selectedHubspotSyncTrigger },
			}).unwrap();
		}
	}, [hasSyncTriggerChanged, selectedHubspotSyncTrigger, listId, updateListSyncTrigger]);

	return {
		// Feature flags
		isHubspotEnabled,
		isConnected,

		// Sync trigger state
		listSyncTrigger,
		selectedHubspotSyncTrigger,
		setSelectedHubspotSyncTrigger,

		// Actions
		hasSyncTriggerChanged,
		saveSyncTrigger,
	};
};
